import {
  type Account,
  type InventoryResource,
  type Snapshot,
  Provider,
} from '@repo/api-gw-sdk';

import { useEnvironment } from '@/contexts/useEnvironment';

import { ManagedDatabaseConfiguration } from './managedDatabaseConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreAzureSqlDBState {
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  resourceGroup?: string;
  location?: string;
  serverName?: string;
  adminLogin?: string;
  SKU?: string;
  keepOriginalTags: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
}

const restoreAzureSqlDBFlow: Step<RestoreAzureSqlDBState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'azure_sql_db_configuration',
      Component: ManagedDatabaseConfiguration,
    },
  ],
};

export const RestoreAzureSqlDBWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { isDemo, isDev } = useEnvironment();

  return (
    <Wizard<RestoreAzureSqlDBState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreAzureSqlDBFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : [Provider.Azure],
        serverName: resource.resourceName.split('/')[0] ?? '',
        adminLogin: snapshot.resource?.properties?.azureMssql?.adminLogin ?? '',
        SKU: snapshot.resource?.properties?.azureMssql?.sku ?? '',
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        location: resource.region,
      }}
      title={'Restore Sql Database'}
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
